/**
 * SAM-944 (mocks M4 and M5): on a phone, desktop-only routes show «Use a desktop for this» and read-only routes
 * show a «View only on a phone» banner above the page. Everywhere else, and on a tablet or desktop, it is invisible:
 * the children render exactly as they did.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Copy, Monitor } from 'lucide-react';
import { useIsPhone } from '../../hooks/useIsPhone';
import { useHeaderTitle } from '../../hooks/useHeaderTitle';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { matrixRoleOf, roleCan } from '../../utils/frontDeskMatrix';
import { phoneAccessFor } from '../../utils/phoneAccess';
import { toaster } from '../ui/toast';

// SAM-1312 (U8): a business's file asks for a desktop on its Services and Settings tabs; its Activity works on a phone.
const BUSINESS_FILE = /^\/business\/(?!list$|new$)([^/]+)\/?$/;

/** SAM-1343: «Cap Cana Admin» when the tenant's own name already ends in Admin; never «Cap Cana Admin Admin». */
export const adminAppName = (name: string): string => (name ? `${name.replace(/\s+admin$/i, '')} Admin` : 'the admin');

const DesktopOnly: React.FC<{ label: string }> = ({ label }) => {
  // SAM-1323: the name the header shows (the business, the estate group, else the tenant), not always the tenant's.
  const headerName = useHeaderTitle('');
  const { pathname } = useLocation();
  const businessId = BUSINESS_FILE.exec(pathname)?.[1];
  // SAM-1321 (U42): a role that only reads Estates (the estate group Front Desk) is not told it can make changes there.
  const perms = useUserPermissions();
  const role = matrixRoleOf(perms);
  const readOnly = !!role && /^\/estates(\/|$)/.test(pathname) && !roleCan(role, 'estates', 'edit');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toaster.positive('Link copied', { autoHideDuration: 2000 });
    } catch {
      toaster.negative('Could not copy the link', { autoHideDuration: 3000 });
    }
  };
  return (
    <main className="flex h-full flex-col items-center justify-center gap-3.5 overflow-auto px-7 pb-20 pt-6 text-center">
      <span className="flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#E7F0EE] text-[var(--tenant-primary)]">
        <Monitor size={32} strokeWidth={1.8} aria-hidden />
      </span>
      <h1 className="m-0 text-[22px] font-bold">Use a desktop for this</h1>
      <p className="m-0 text-[15px] leading-normal text-[var(--ink-700)]">
        {label} needs a bigger screen. Open {adminAppName(headerName)} on a computer {readOnly ? 'to see it' : 'to make changes'}.
      </p>
      <div className="mt-2.5 flex w-full flex-col gap-2.5">
        <Link
          to={businessId ? `/business/${businessId}` : '/dashboard'}
          className="flex h-12 items-center justify-center rounded-lg bg-[var(--tenant-primary)] text-[15px] font-semibold text-white no-underline"
        >
          {businessId ? 'Open Activity' : 'Back to Dashboard'}
        </Link>
        <button
          type="button"
          onClick={copy}
          className="flex h-12 items-center justify-center gap-2 rounded-lg border border-solid border-[var(--line)] bg-white text-[15px] font-semibold text-[var(--ink-700)]"
        >
          <Copy size={16} aria-hidden /> Copy link
        </button>
      </div>
    </main>
  );
};

export const ViewOnlyBanner: React.FC = () => (
  <div
    role="status"
    className="flex shrink-0 items-start gap-2.5 border-0 border-b border-solid border-[#BAE6FD] bg-[#F0F9FF] px-3.5 py-3 text-sm leading-[1.4] text-[#0369A1]"
  >
    <Monitor size={18} className="mt-px shrink-0" aria-hidden />
    <span>View only on a phone. Use a desktop to edit.</span>
  </div>
);

export const PhoneAccessGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const phone = useIsPhone();
  const { pathname, search } = useLocation();
  if (!phone) return <>{children}</>;
  const { access, label } = phoneAccessFor(pathname, search);
  if (access === 'desktop') return <DesktopOnly label={label} />;
  if (access === 'view') {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <ViewOnlyBanner />
        <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      </div>
    );
  }
  return <>{children}</>;
};
